<template>
  <teleport to="body">
    <div v-if="visible" v-bind="$attrs" class="modal-overlay" :class="[modalWrapClass, { 'visible': animate, 'modal-bottom': modalToBottom, 'tooltip': isTooltip}]" @click.self="closeModal">
      <div class="modal-content" :class="[modalClass, { 'in': animateIn }]">
        <div v-if="!nowrap">
          <div class="modal-header">
            <slot name="header">
              <h4 class="modal-title">{{ modalTitle }}</h4>
            </slot>
            <button class="button modal-button modal-button--close" type="button" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <slot name="body"></slot>
          </div>
        </div>
        <slot v-else></slot>
      </div>
    </div>
  </teleport>
</template>

  <script>
  export default {
    name: 'ChatModal',
    inheritAttrs: false,
    props: {
      show: {
        type: Boolean,
        required: true
      },
      inHeader: {
        type: Boolean,
        required: false
      },
      doubleModal: {
        type: Boolean,
        default: false
      },
      modalToBottom: {
        type: Boolean,
        default: false
      },
      modalTitle: {
        type: String,
        required: false
      },
      modalClass: {
        type: [String, Array],
        default: ''
      },
      modalWrapClass: {
        type: String,
        default: ''
      },
      isTooltip: {
        type: Boolean,
        default: false,
      },
      nowrap: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        visible: false,
        animate: false,
        animateIn: false,
      };
    },
    watch: {
      show: {
        immediate: true,
        handler(val) {
          if (val) {
            this.open();
          } else if (this.visible) {
            this.close();
          }
        }
      }
    },
    methods: {
      open() {
        this.visible = true;
        this.toggleBody(true);
        requestAnimationFrame(() => {
          this.animate = true;
          setTimeout(()=>{
            this.animateIn = true;
          },5)
        });
      },

      close() {
        this.animateIn = false;
        setTimeout(()=>{
          this.animate = false;
        },5)
        this.toggleBody(false);

        setTimeout(() => {
          this.visible = false;
        }, 250);
      },

      closeModal() {
        this.$emit('close');
      },

      toggleBody(state) {
        const header = document.querySelector('.header-inner');

        if (state) {
            if (!this.doubleModal) {
                document.body.classList.add('modal-open');
            }

            if (!this.inHeader && header) {
              header.classList.add('modal-open');
              if(this.isTooltip) {
                header.classList.add('modal-tooltip');
              }
            }

            return;
        }

        setTimeout(() => {
            const openedModals = document.querySelectorAll('.modal-overlay.visible');

            if (openedModals.length > 1) {
                return;
            }

            document.body.classList.remove('modal-open');

            if (!this.inHeader && header) {
                header.classList.remove('modal-open');
                if(this.isTooltip) {
                  header.classList.remove('modal-tooltip');
                }
            }
        }, 0);
      }
    },
    beforeUnmount() {
      this.toggleBody(false);
    }
  };
  </script>
  
  <style scoped lang="scss">
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: var(--wc-backdrop);
    transition: opacity 0.3s ease;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    opacity: 0;
    visibility: hidden;
    @media (max-width: 767px) {
      z-index: 1000;
      top: 70px;
      justify-content: flex-start;
      align-items: flex-end;
    }
    @media (max-width: 767px) {
      top: 43px;
      height: calc(100dvh - 43px);
      &.tooltip{
        top: 0;
        height: 100dvh;
      }
    }
    &.modal-bottom{
      @media (max-width: 767px) {
        align-items: flex-end;
        bottom: 0;
      }
    }
    &.tooltip{
      top: 0;
      height: 100dvh;
    }
    &.visible {
      opacity: 1;
      visibility: visible;
    }
    &.fade-disable {
      background: transparent;
      backdrop-filter: blur(0);
    }
  }
  
  .modal-content {
    background: var(--wc-surface);
    border-radius: 16px;
    box-shadow: 0 24px 64px rgba(15, 23, 41, 0.24);
    transform: translateY(20%);
    transition: 0.5s all !important;
    width: 100%;
    max-width: 520px;
    position: relative;
    max-height: calc(100dvh - 100px);
    overflow-y: auto;
    opacity: 0;
    font-family: var(--wc-font);
    color: var(--wc-text);
    @media (max-width: 767px) {
      width: 100%;
      border-radius: 16px 16px 0 0;
      //height: 100%;
      max-width: 100%;
      max-height: calc(100dvh - 110px);
    }
    &.in{
      opacity: 1;
      transform: translateY(0);
    }
    .modal-header{
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 56px;
      padding: 0 56px 0 20px;
      position: relative;
      border-bottom: 1px solid var(--wc-surface-2);
      &::before,
      &::after{
        content: none;
      }
      :deep(.modal-title){
        margin: 0;
        padding: 0;
        font-family: var(--wc-font-heading);
        font-size: 16px;
        line-height: 24px;
        font-weight: 400;
        color: var(--wc-text);
        text-align: left;
      }
    }
    .modal-body{

    }
    
    .modal-button {
      background: transparent;
      border: none;
      box-shadow: none;
      margin: 0;
      outline: none;
      padding: 0;
  
      &:hover {
        background: transparent;
      }
  
      &--close {
        display: inline-block;
        width: 40px;
        height: 40px;
        border-radius: 8px;
        position: absolute;
        right: 8px;
        top: 8px;
        transition: 0.2s all;
        z-index: 1;
        &:hover{
          background: var(--wc-bg);
        }
  
        &:before,
        &:after {
          content: '';
          background: var(--wc-text-2);
          display: block;
          position: absolute;
          width: 14px;
          height: 1.75px;
          border-radius: 1px;
          left: 13px;
          top: 50%;
          transform-origin: center center;
        }
  
        &:before {
          transform: rotate(45deg);
        }
  
        &:after {
          transform: rotate(-45deg);
        }
      }
    }
  }
  
  .modal-close {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
  }
  </style>